import React, { useState } from "react";
import "./UserForm.css";
import ActionBar from "../Components/BottomActionBar/ActionBar";
import UpdateComponent from "../Components/UpdateComponent/UpdateComponent";
import { userAPI } from "./api";
import DataTable from "./DataTable";

function UserForm() {
  const [salCode, setSalCode] = useState("");
  const [roleType, setRoleType] = useState("");
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditCols, setShowEditCols] = useState(false);
  const [editRows, setEditRows] = useState({});
  const [selectedForUpdate, setSelectedForUpdate] = useState({});
  const [selectedForDelete, setSelectedForDelete] = useState({});
  const [insertMsg, setInsertMsg] = useState(null);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [sortBy, setSortBy] = useState("");

  const roleTypeOptions = [
    { value: "SU", label: "Super User" },
    { value: "CU", label: "Common User" },
  ];

  const clearMessages = () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
  };

  const buildParams = () => {
    const params = {};
    if (salCode.trim()) params.sal_code = salCode.trim();
    if (roleType.trim()) params.role_type = roleType;
    return params;
  };

  const handleInsert = async () => {
    setUpdateMsg(null);
    setDeleteMsg(null);

    if (!salCode.trim() || !roleType) {
      alert("Salary Code and Type of User are required fields.");
      return;
    }

    try {
      const res = await userAPI.create({
        sal_code: Number(salCode.trim()),
        role_type: roleType,
      });
      if (res.ok) {
        const data = await userAPI.getAll(buildParams()).catch(() => []);
        setTableData(Array.isArray(data) ? data : []);
        setInsertMsg({ text: "Record inserted successfully.", success: true });
      }
      else setInsertMsg({ text: "Insert failed.", success: false });
    } catch {
      setInsertMsg({ text: "Insert failed.", success: false });
    }
  };

  const handleUpdate = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(true);
    setEditRows({});

    try {
      const data = await userAPI.getAll(buildParams());
      setTableData(Array.isArray(data) ? data : []);
      setShowTable(true);
    } catch {
      setTableData([]);
      setShowTable(true);
    }
  };

  const handleView = async () => {
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setShowEditCols(false);
    setEditRows({});

    try {
      const data = await userAPI.getAll(buildParams());
      setTableData(Array.isArray(data) ? data : []);
      setShowTable(true);
    } catch {
      setTableData([]);
      setShowTable(true);
    }
  };

  const toggleEditRow = (idx, row) => {
    setEditRows((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return {
        ...prev,
        [idx]: {
          role_type: (row.role_type ?? "").trim(),
        },
      };
    });

    setSelectedForUpdate((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const toggleDeleteRow = (idx) => {
    setSelectedForDelete((prev) => {
      if (prev[idx]) {
        const next = { ...prev };
        delete next[idx];
        return next;
      }
      return { ...prev, [idx]: true };
    });
  };

  const handleUpdateSelected = async () => {
    const selectedIndexes = Object.keys(selectedForUpdate).filter((i) => selectedForUpdate[i]);

    if (selectedIndexes.length === 0) {
      setUpdateMsg({ text: "No records selected for update.", success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      const editedData = editRows[idx];
      if (!editedData) continue;

      try {
        const res = await userAPI.update(row.sal_code, row.role_type, {
          role_type: editedData.role_type,
        });

        if (res.ok) successCount++;
        else {
          await res.json().catch(() => ({}));
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setUpdateMsg({ text: `${successCount} record(s) updated successfully.`, success: true });
      const data = await userAPI.getAll(buildParams()).catch(() => []);
      setTableData(Array.isArray(data) ? data : []);
    }
    if (failCount > 0) {
      setUpdateMsg({ text: `${failCount} record(s) failed to update.`, success: false });
    }

    setSelectedForUpdate({});
    setEditRows({});
  };

  const handleDeleteSelected = async () => {
    const selectedIndexes = Object.keys(selectedForDelete).filter((i) => selectedForDelete[i]);

    if (selectedIndexes.length === 0) {
      setDeleteMsg({ text: "No records selected for delete.", success: false });
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const idx of selectedIndexes) {
      const row = tableData[idx];
      try {
        const res = await userAPI.delete(row.sal_code, row.role_type);
        if (res.ok) successCount++;
        else {
          await res.json().catch(() => ({}));
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      setDeleteMsg({ text: `${successCount} record(s) deleted successfully.`, success: true });
      const data = await userAPI.getAll(buildParams()).catch(() => []);
      setTableData(Array.isArray(data) ? data : []);
    }
    if (failCount > 0) {
      setDeleteMsg({ text: `${failCount} record(s) failed to delete.`, success: false });
    }

    setSelectedForDelete({});
  };

  const handleReset = () => {
    setSalCode("");
    setRoleType("");
    setShowEditCols(false);
    setEditRows({});
    setSelectedForUpdate({});
    setSelectedForDelete({});
    setInsertMsg(null);
    setUpdateMsg(null);
    setDeleteMsg(null);
    setTableData([]);
    setShowTable(false);
    setSortBy("");
  };

  const sortTableData = (data, sortOption) => {
    if (!sortOption) return data;
    const sorted = [...data];
    switch (sortOption) {
      case "Salary Code":
        return sorted.sort((a, b) => Number(a.sal_code || 0) - Number(b.sal_code || 0));
      case "Type of User":
        return sorted.sort((a, b) => String(a.role_type || "").trim().localeCompare(String(b.role_type || "").trim()));
      default:
        return sorted;
    }
  };

  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (tableData.length > 0) {
      setTableData(sortTableData(tableData, sortOption));
    }
  };

  return (
    <div className="ufBody" onClick={clearMessages}>
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="ufFormRow">
          <label className="ufLabel">
            Salary Code<span className="ufImportant">*</span>
          </label>
          <input
            className="ufSalaryInput"
            type="text"
            value={salCode}
            onChange={(e) => setSalCode(e.target.value)}
            onFocus={clearMessages}
          />
        </div>

        <div className="ufFormRow">
          <label className="ufLabel">
            Type of User<span className="ufImportant">*</span>
          </label>
          <select
            className="ufTypeSelect"
            value={roleType}
            onChange={(e) => setRoleType(e.target.value)}
            onFocus={clearMessages}
          >
            <option value="">Select Type of User</option>
            {roleTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="ufFormRow">
          <p className="ufRemarks">
            <span className="ufImportant">*</span> indicates a required field for 'INSERT' &amp; 'UPDATE'.
          </p>
        </div>
      </form>

      <ActionBar
        sortOptions={["Salary Code", "Type of User"]}
        onView={handleView}
        onReset={handleReset}
        onInsert={handleInsert}
        onUpdate={handleUpdate}
        onSortChange={handleSortChange}
        selectedSort={sortBy}
      />

      {insertMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: insertMsg.success ? "green" : "red", margin: "4px 0" }}>
          {insertMsg.text}
        </p>
      )}
      {updateMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: updateMsg.success ? "green" : "red", margin: "4px 0" }}>
          {updateMsg.text}
        </p>
      )}
      {deleteMsg && (
        <p style={{ textAlign: "center", fontSize: "11px", fontFamily: '"GEG-Body"', color: deleteMsg.success ? "green" : "red", margin: "4px 0" }}>
          {deleteMsg.text}
        </p>
      )}

      {showTable && (
        <DataTable
          columns={[
            { header: "Salary Code", key: "sal_code", accessor: (row) => row.sal_code ?? "" },
            {
              header: "Type of User",
              key: "role_type",
              accessor: (row) => row.role_type ?? "",
              editable: true,
            },
          ]}
          data={tableData}
          showEditCols={showEditCols}
          editRows={editRows}
          onToggleEditRow={toggleEditRow}
          onChangeEditCell={(idx, key, value) =>
            setEditRows((prev) => ({ ...prev, [idx]: { ...prev[idx], [key]: value } }))
          }
          selectedForDelete={selectedForDelete}
          onToggleDeleteRow={toggleDeleteRow}
          emptyMessage="No data found"
          rowKey={(row, idx) => `${row.sal_code}-${idx}`}
        />
      )}

      {showTable && showEditCols && (
        <UpdateComponent onUpdateSelected={handleUpdateSelected} onDeleteSelected={handleDeleteSelected} />
      )}
    </div>
  );
}

export default UserForm;
